import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IDispatchInvoice, IDispatchInvoiceGenerateResult } from "common/interface/IDispatchInvoice";
import { IApiError } from "common/interface/IApiError";
import { logoutIfNeeded } from "common/utils";

interface DispatchInvoiceState {
  invoices: IDispatchInvoice[];
  status: "idle" | "loading" | "success" | "error";
  error?: string;
  generateResult: IDispatchInvoiceGenerateResult | null;
  generateStatus: "idle" | "loading" | "success" | "error";
  generateError?: string;
}

const initialState: DispatchInvoiceState = {
  invoices: [],
  status: "idle",
  generateResult: null,
  generateStatus: "idle",
};

export const getDispatchInvoices = createAsyncThunk<IDispatchInvoice[], { date: string }>(
  "dispatchInvoice/list",
  async ({ date }, thunkApi) => {
    const response = await API.get(apis.getDispatchInvoices, { params: { date } })
      .then((res) => res.data)
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

export const generateDispatchInvoice = createAsyncThunk<
  IDispatchInvoiceGenerateResult,
  { dispatchId: number }
>(
  "dispatchInvoice/generate",
  async ({ dispatchId }, thunkApi) => {
    const response = await API.post(apis.generateDispatchInvoices, { dispatchIds: [dispatchId] })
      .then((res) => {
        const results: IDispatchInvoiceGenerateResult[] = res.data?.results ?? [];
        const result = results.find((r) => r.dispatchId === dispatchId) ?? results[0];
        if (!result) throw new Error("No result returned");
        return result;
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

const dispatchInvoiceSlice = createSlice({
  name: "dispatchInvoice",
  initialState,
  reducers: {
    clearGenerateResult(state) {
      state.generateResult = null;
      state.generateStatus = "idle";
      state.generateError = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getDispatchInvoices.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(getDispatchInvoices.fulfilled, (state, action) => {
        state.status = "success";
        state.invoices = action.payload ?? [];
      })
      .addCase(getDispatchInvoices.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        state.error = error?.message;
      })
      .addCase(generateDispatchInvoice.pending, (state) => {
        state.generateStatus = "loading";
        state.generateResult = null;
        state.generateError = undefined;
      })
      .addCase(generateDispatchInvoice.fulfilled, (state, action) => {
        state.generateStatus = "success";
        state.generateResult = action.payload;
      })
      .addCase(generateDispatchInvoice.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.generateStatus = "error";
        state.generateError = error?.message;
      });
  },
});

export const { clearGenerateResult } = dispatchInvoiceSlice.actions;
export default dispatchInvoiceSlice.reducer;
